import { Component, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { fas } from '@fortawesome/pro-solid-svg-icons';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { MessageService } from 'primeng/api';
import { LayoutService } from '@/layout/service/layout.service';
import { TranslationService } from '@/layout/service/translation.service';
import { AppVersionService } from './services/app-version.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    @if (appVersion.updateAvailable()) {
      <div class="app-update-banner" role="status" aria-live="polite">
        <i class="pi pi-refresh" aria-hidden="true"></i>
        <span class="app-update-text">
          <strong>{{ t()('appUpdate.available') }}</strong>
          <span class="app-update-hint">{{ t()('appUpdate.autoReload') }}</span>
        </span>
        <button type="button" class="app-update-button" (click)="appVersion.reloadNow()">{{ t()('appUpdate.reload') }}</button>
      </div>
    }
    <router-outlet></router-outlet>
  `,
  styles: [
    `
      .app-update-banner {
        position: fixed;
        top: 0.75rem;
        left: 50%;
        transform: translateX(-50%);
        z-index: 1200;
        display: flex;
        align-items: center;
        gap: 0.75rem;
        max-width: calc(100vw - 2rem);
        padding: 0.625rem 0.75rem 0.625rem 1rem;
        border-radius: var(--p-border-radius-lg, 8px);
        background: var(--p-primary-color);
        color: var(--p-primary-contrast-color);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
      }
      .app-update-text {
        display: flex;
        flex-direction: column;
        line-height: 1.3;
      }
      .app-update-hint {
        font-size: 0.85em;
        opacity: 0.9;
      }
      .app-update-button {
        flex-shrink: 0;
        padding: 0.375rem 0.875rem;
        border: 1px solid currentColor;
        border-radius: var(--p-border-radius-md, 6px);
        background: transparent;
        color: inherit;
        font: inherit;
        font-weight: 600;
        cursor: pointer;
      }
      .app-update-button:hover {
        background: rgba(255, 255, 255, 0.15);
      }
    `
  ],
  providers: [MessageService]
})
export class CloudAppComponent {
  isProd = !!(window as any).ngDevMode === false;

  /**
   * Injected here (not only in AppLayout) so the colour scheme is applied on routes that live outside
   * the shell — login, OAuth callback, mobile auth — and body-appended overlays there inherit it.
   */
  private readonly layout = inject(LayoutService);

  /** Detects a redeploy while this tab is open and prompts (then forces) a reload. */
  protected readonly appVersion = inject(AppVersionService);
  protected readonly t = inject(TranslationService).t;

  constructor(library: FaIconLibrary, private messageService: MessageService) {
    library.addIconPacks(fas);
    this.appVersion.start();
  }
}
